import React, { useState, useEffect } from "react";
import Logo from "../../assets/logo.png";
import "./DashboardPage.css";
import {
  fetchAllOrders,
  buildLocationMap,
  buildCategoryMetrics,
} from "../../services/api";
import ExecutiveSummary from "./ExecutiveSummary";
import LocationsView from "./LocationsView";
import OrdersView from "./OrdersView";
import ReportsView from "./ReportsView";
import CategoryMetrics from "./CategoryMetrics";
import { filterOrdersByNairobiTime } from "../../timeUtilis";

// Helper function to adjust durations
const adjustDurations = (orders, adjustEnabled) => {
  if (!adjustEnabled || !orders) return orders;
  
  return orders.map(order => {
    if (!order.billingDuration) return order;
    
    // Cap duration at 3 hours (180 minutes)
    const originalDuration = order.billingDuration;
    const adjustedDuration = Math.min(originalDuration, 180);
    
    // Create a copy of the order with adjusted duration
    return {
      ...order,
      billingDuration: adjustedDuration,
      originalDuration: originalDuration, // Keep original for reference
      isAdjusted: originalDuration > 180, // Flag if it was adjusted
      adjustmentAmount: originalDuration > 180 ? originalDuration - 180 : 0,
      adjustmentPercentage: originalDuration > 180 ? 
        Math.round(((originalDuration - 180) / originalDuration) * 100) : 0
    };
  });
};

// Helper function to format duration for display
const formatDurationForDisplay = (minutes) => {
  if (!minutes || minutes === 0) return "0 min";

  const hours = minutes / 60;

  if (hours < 1) {
    return `${Math.round(minutes)} min`;
  } else {
    const fullHours = Math.floor(hours);
    const remainingMinutes = Math.round((hours - fullHours) * 60);

    if (remainingMinutes === 0) {
      return `${fullHours} hr${fullHours > 1 ? "s" : ""}`;
    } else {
      return `${fullHours} hr${fullHours > 1 ? "s" : ""} ${remainingMinutes} min`;
    }
  }
};

const DashboardPage = ({ activeSection, dateRange, timeRange }) => {
  const getDefaultDateRange = () => {
    const now = new Date();
    const todayMidnight = new Date(now);
    todayMidnight.setHours(0, 0, 0, 0);
    return { start: todayMidnight, end: now };
  };

  // STATES
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [adjustedOrders, setAdjustedOrders] = useState([]);
  const [locationMap, setLocationMap] = useState({});
  const [categoryMetrics, setCategoryMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [adjustDurationsEnabled, setAdjustDurationsEnabled] = useState(false);
  const [showAdjustedOrdersModal, setShowAdjustedOrdersModal] = useState(false);

  const currentDateRange = dateRange || getDefaultDateRange();

  // Toggle for duration adjustment
  const toggleDurationAdjustment = () => {
    setAdjustDurationsEnabled(!adjustDurationsEnabled);
  };

  useEffect(() => {
    loadAllData();
  }, [currentDateRange, timeRange]);

  // Update adjusted orders when toggle changes
  useEffect(() => {
    if (filteredOrders.length > 0) {
      const adjusted = adjustDurations(filteredOrders, adjustDurationsEnabled);
      setAdjustedOrders(adjusted);
    }
  }, [filteredOrders, adjustDurationsEnabled]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      console.log("🔄 Loading data... (Using Nairobi time filtering)");

      const ordersData = await fetchAllOrders(currentDateRange);
      console.log(`✅ Loaded ${ordersData.full.length} full orders`);

      setOrders(ordersData.full);

      const nairobiFiltered = filterOrdersByNairobiTime(
        ordersData.full,
        currentDateRange
      );
      setFilteredOrders(nairobiFiltered);
      
      // Apply adjustment if enabled
      const adjusted = adjustDurations(nairobiFiltered, adjustDurationsEnabled);
      setAdjustedOrders(adjusted);

      // Build metrics using adjusted orders
      const locMap = buildLocationMap(adjusted);
      setLocationMap(locMap);

      const catMetrics = buildCategoryMetrics(adjusted);
      setCategoryMetrics(catMetrics);

      console.log("✅ Dashboard data ready with Nairobi time filtering!");
      console.log(`🔄 Duration adjustment: ${adjustDurationsEnabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (error) {
      console.error("❌ Error loading data:", error);
      setOrders([]);
      setFilteredOrders([]);
      setAdjustedOrders([]);
      setLocationMap({});
      setCategoryMetrics({});
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="company-logos">
            <img src={Logo} alt="Company Logo" className="logo-image" />
          </div>
          <div className="loading-dots">
            <div className="dot dot-1"></div>
            <div className="dot dot-2"></div>
            <div className="dot dot-3"></div>
            <div className="dot dot-4"></div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate adjustment stats for the button
  const calculateAdjustmentStats = () => {
    if (!filteredOrders.length) return { 
      adjustedCount: 0, 
      totalAdjustedMinutes: 0,
      adjustedOrders: []
    };
    
    const ordersWithDuration = filteredOrders.filter(o => o.billingDuration > 0);
    const adjustedOrdersList = ordersWithDuration.filter(o => o.billingDuration > 180);
    const totalAdjustedMinutes = adjustedOrdersList.reduce((sum, o) => sum + (o.billingDuration - 180), 0);
    
    return {
      adjustedCount: adjustedOrdersList.length,
      totalAdjustedMinutes: totalAdjustedMinutes,
      adjustedOrders: adjustedOrdersList
    };
  };

  const adjustmentStats = calculateAdjustmentStats();

  // Get adjusted orders for modal (with both original and adjusted durations)
  const getAdjustedOrdersForModal = () => {
    return adjustmentStats.adjustedOrders.map(order => ({
      ...order,
      adjustedDuration: Math.min(order.billingDuration, 180),
      originalDuration: order.billingDuration
    })).sort((a, b) => b.originalDuration - a.originalDuration); // Sort by longest original duration first
  };

  const renderSection = () => {
    // Use adjusted orders if adjustment is enabled
    const ordersToUse = adjustDurationsEnabled ? adjustedOrders : filteredOrders;
    
    return (
      <>
        {/* Adjusted Orders Modal */}
        {showAdjustedOrdersModal && (
          <div className="adjusted-orders-modal-overlay" onClick={() => setShowAdjustedOrdersModal(false)}>
            <div className="adjusted-orders-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <i className="fas fa-clock"></i> Adjusted Orders Summary
                </h2>
                <button 
                  className="modal-close-btn"
                  onClick={() => setShowAdjustedOrdersModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="modal-summary">
                <div className="modal-summary-stats">
                  <div className="summary-stat">
                    <span className="stat-value">{adjustmentStats.adjustedCount}</span>
                    <span className="stat-label">Orders Adjusted</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{formatDurationForDisplay(adjustmentStats.totalAdjustedMinutes)}</span>
                    <span className="stat-label">Total Reduction</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">
                      {adjustmentStats.adjustedCount > 0 
                        ? formatDurationForDisplay(adjustmentStats.totalAdjustedMinutes / adjustmentStats.adjustedCount)
                        : "0 min"
                      }
                    </span>
                    <span className="stat-label">Avg Reduction per Order</span>
                  </div>
                </div>
              </div>

              <div className="modal-table-container">
                <table className="adjusted-orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Location</th>
                      <th>Original Duration</th>
                      <th>Adjusted Duration</th>
                      <th>Reduction</th>
                      <th>% Reduced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getAdjustedOrdersForModal().map((order, index) => {
                      const reduction = order.originalDuration - order.adjustedDuration;
                      const reductionPercentage = Math.round((reduction / order.originalDuration) * 100);
                      
                      return (
                        <tr key={order.id || index} className="adjusted-order-row">
                          <td>
                            <span className="order-id">
                              #{order.pOrderid || order.pAliorderid || index + 1}
                            </span>
                          </td>
                          <td>
                            <div className="location-cell">
                              <i className="fas fa-map-marker-alt"></i>
                              {order.pShopName || "Unassigned"}
                            </div>
                          </td>
                          <td>
                            <span className="original-duration" title={`${order.originalDuration} minutes`}>
                              {formatDurationForDisplay(order.originalDuration)}
                            </span>
                          </td>
                          <td>
                            <span className="adjusted-duration" title={`${order.adjustedDuration} minutes`}>
                              {formatDurationForDisplay(order.adjustedDuration)}
                              
                            </span>
                          </td>
                          <td>
                            <span className="reduction-amount" title={`${reduction} minutes`}>
                              {formatDurationForDisplay(reduction)}
                            </span>
                          </td>
                          <td>
                            <span className={`reduction-percentage ${reductionPercentage > 50 ? 'high' : reductionPercentage > 25 ? 'medium' : 'low'}`}>
                              {reductionPercentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {adjustmentStats.adjustedCount === 0 && (
                  <div className="no-adjusted-orders">
                    <i className="fas fa-check-circle"></i>
                    <h3>No Adjusted Orders</h3>
                    <p>All orders are within the 3-hour limit.</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <p className="modal-note">
                  <i className="fas fa-info-circle"></i>
                  Note: All durations over 3 hours (180 minutes) are capped at the maximum allowed time.
                </p>
                <button 
                  className="modal-close-button"
                  onClick={() => setShowAdjustedOrdersModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {(() => {
          switch (activeSection) {
            case "overview":
              return (
                <>
                  <div className="adjustment-controls">
                    <button 
                      className={`adjust-toggle-btn ${adjustDurationsEnabled ? 'active' : ''}`}
                      onClick={toggleDurationAdjustment}
                      title={adjustDurationsEnabled 
                        ? "Click to show actual durations" 
                        : "Click to cap durations at 3 hours (180 min)"
                      }
                    >
                      <i className={`fas fa-${adjustDurationsEnabled ? 'check-circle' : 'clock'}`}></i>
                      <span className="adjust-toggle-text">
                        {adjustDurationsEnabled ? 'Showing Adjusted Durations' : 'Adjust Durations'}
                      </span>
                      {/* {adjustmentStats.adjustedCount > 0 && (
                        <span className="adjustment-badge">
                          {adjustmentStats.adjustedCount} orders adjusted
                        </span>
                      )} */}
                    </button>
                    
                    {adjustDurationsEnabled && adjustmentStats.adjustedCount > 0 && (
                      <div 
                        className="adjustment-info clickable"
                        onClick={() => setShowAdjustedOrdersModal(true)}
                        title="Click to view adjusted orders details"
                      >
                        <i className="fas fa-info-circle"></i>
                        <span>
                          {adjustmentStats.adjustedCount} order(s) capped at 3hrs.
                          Total reduction: {Math.round(adjustmentStats.totalAdjustedMinutes / 60 * 10) / 10} hours
                          <span className="click-hint"> (Click for details)</span>
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <ExecutiveSummary
                    orders={ordersToUse}
                    dateRange={currentDateRange}
                    adjustDurationsEnabled={adjustDurationsEnabled}
                  />
                  <CategoryMetrics
                    orders={ordersToUse}
                    categoryMetrics={categoryMetrics}
                    adjustDurationsEnabled={adjustDurationsEnabled}
                  />
                  <LocationsView
                    orders={ordersToUse}
                    locationMap={locationMap}
                    adjustDurationsEnabled={adjustDurationsEnabled}
                  />
                </>
              );
            case "locations":
              return (
                <LocationsView
                  orders={ordersToUse}
                  locationMap={locationMap}
                  adjustDurationsEnabled={adjustDurationsEnabled}
                />
              );
            case "orders":
              return (
                <OrdersView
                  orders={ordersToUse}
                  locationMap={locationMap}
                  categoryMetrics={categoryMetrics}
                  dateRange={currentDateRange}
                  adjustDurationsEnabled={adjustDurationsEnabled}
                />
              );
            case "reports":
              return (
                <ReportsView
                  orders={ordersToUse}
                  dateRange={currentDateRange}
                  locationMap={locationMap}
                  categoryMetrics={categoryMetrics}
                />
              );
            default:
              return null;
          }
        })()}
      </>
    );
  };

  return <div className="dashboard-page">{renderSection()}</div>;
};

export default DashboardPage;